import { useFormik } from 'formik'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'

import { EuiButton, EuiLoadingContent, EuiOverlayMask, EuiModalHeader, EuiModalHeaderTitle, EuiModalBody, EuiForm, EuiFormRow, EuiFieldText, EuiFieldPassword, EuiSpacer, EuiModalFooter, EuiButtonEmpty, EuiModal, EuiBasicTable, EuiCheckboxGroup, EuiSelect, EuiText } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { ApiError, apiPostOrganizationUsers } from 'api/adcritterApi'
import { OrganizationUser, UserRole, UserType } from 'api/interfaces'
import { useGetOrganizationUsersQuery, useUpdateOrganizationUserRolesMutation } from 'api/rtkQueryApi/platform/organizationsApi'
import { showErrorToast, showSuccessToast } from 'app/appSlice'
import { RootState } from 'app/rootReducer'
import { useCampaignApproval } from 'hooks/useCampaignApproval'

interface FormValues {
  firstName: string
  lastName: string
  email: string
  password: string
}

interface UserAccessDraft {
  userType: UserType
  userRoles: UserRole[]
}

const userTypeOptions = [
  { value: UserType.User, text: 'User' },
  { value: UserType.PlatformAdmin, text: 'Platform Admin' }
]

const roleCheckboxLabels: Array<{ role: UserRole; label: string }> = [
  { role: UserRole.Create, label: 'Create' },
  { role: UserRole.Approve, label: 'Approve' }
]

const roleCheckboxId = (userId: string, role: UserRole) => `${userId}-${role}`

const roleCheckboxOptionsForUser = (userId: string) => roleCheckboxLabels.map(option => ({ id: roleCheckboxId(userId, option.role), label: option.label }))

const roleFromCheckboxId = (userId: string, checkboxId: string) => checkboxId.substring(userId.length + 1) as UserRole

const userTypeLabel = (userType: UserType) => (userType === UserType.PlatformAdmin ? 'Platform Admin' : 'User')

const draftFromUser = (user: OrganizationUser): UserAccessDraft => ({
  userType: user.userType,
  userRoles: user.userRoles
})

const haveSameRoles = (left: UserRole[], right: UserRole[]) => left.length === right.length && left.every(role => right.includes(role))

const draftMatchesUser = (draft: UserAccessDraft, user: OrganizationUser) => draft.userType === user.userType && haveSameRoles(draft.userRoles, user.userRoles)

const UsersDrawer = () => {
  const dispatch = useDispatch()
  const { currentUser } = useSelector((state: RootState) => state.app)
  const usersQuery = useGetOrganizationUsersQuery()
  const [updateUserRoles] = useUpdateOrganizationUserRolesMutation()
  const { isEnabled: campaignApprovalEnabled, canManageUsers } = useCampaignApproval()
  const canEditUserAccess = campaignApprovalEnabled && canManageUsers
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [drafts, setDrafts] = useState<Record<string, UserAccessDraft>>({})
  const [savingUserId, setSavingUserId] = useState<string | null>(null)

  const findUser = (userId: string) => usersQuery.data?.find(user => user.id === userId)

  const applyDraftChange = (userId: string, change: (draft: UserAccessDraft) => UserAccessDraft) => {
    const user = findUser(userId)
    if (!user) {
      return
    }

    setDrafts(previous => {
      const nextDraft = change(previous[userId] ?? draftFromUser(user))
      const { [userId]: currentDraft, ...remaining } = previous
      return draftMatchesUser(nextDraft, user) ? remaining : { ...remaining, [userId]: nextDraft }
    })
  }

  const clearDraft = (userId: string) => {
    setDrafts(previous => {
      const { [userId]: removedDraft, ...remaining } = previous
      return remaining
    })
  }

  const updateDraftUserType = (userId: string, userType: UserType) => {
    applyDraftChange(userId, draft => ({ ...draft, userType }))
  }

  const toggleDraftRole = (userId: string, role: UserRole) => {
    applyDraftChange(userId, draft => {
      const hasRole = draft.userRoles.includes(role)
      const userRoles = hasRole ? draft.userRoles.filter(existingRole => existingRole !== role) : [...draft.userRoles, role]
      return { ...draft, userRoles }
    })
  }

  const saveUserAccess = async (userId: string) => {
    const draft = drafts[userId]
    if (!draft) {
      return
    }

    setSavingUserId(userId)
    try {
      await updateUserRoles({ userId, userType: draft.userType, userRoles: draft.userRoles }).unwrap()
      clearDraft(userId)
      dispatch(showSuccessToast('Updated user access'))
    } catch {
      dispatch(showErrorToast('Failed to update user access'))
    } finally {
      setSavingUserId(null)
    }
  }

  const isSelf = (user: OrganizationUser) => currentUser?.email === user.email

  const accessColumns: Array<EuiBasicTableColumn<OrganizationUser>> = [
    {
      name: 'User Type',
      render: (user: OrganizationUser) => {
        const draft = drafts[user.id] ?? draftFromUser(user)
        if (isSelf(user)) {
          return <EuiText size='s'>{userTypeLabel(draft.userType)}</EuiText>
        }
        return <EuiSelect compressed options={userTypeOptions} value={draft.userType} onChange={event => updateDraftUserType(user.id, event.target.value as UserType)} />
      }
    },
    {
      name: 'Roles',
      render: (user: OrganizationUser) => {
        const draft = drafts[user.id] ?? draftFromUser(user)
        if (isSelf(user)) {
          return <EuiText size='s'>{draft.userRoles.length > 0 ? draft.userRoles.join(', ') : 'None'}</EuiText>
        }
        return (
          <EuiCheckboxGroup
            options={roleCheckboxOptionsForUser(user.id)}
            idToSelectedMap={{
              [roleCheckboxId(user.id, UserRole.Create)]: draft.userRoles.includes(UserRole.Create),
              [roleCheckboxId(user.id, UserRole.Approve)]: draft.userRoles.includes(UserRole.Approve)
            }}
            onChange={checkboxId => toggleDraftRole(user.id, roleFromCheckboxId(user.id, checkboxId))}
          />
        )
      }
    },
    {
      name: '',
      render: (user: OrganizationUser) => {
        if (isSelf(user)) {
          return (
            <EuiText size='xs' color='subdued'>
              You cannot change your own access
            </EuiText>
          )
        }
        return (
          <EuiButton size='s' onClick={() => saveUserAccess(user.id)} isLoading={savingUserId === user.id} isDisabled={!drafts[user.id]}>
            Save
          </EuiButton>
        )
      }
    }
  ]

  const columns: Array<EuiBasicTableColumn<OrganizationUser>> = [
    {
      name: 'Name',
      field: 'name'
    },
    {
      name: 'Email',
      field: 'email'
    },
    ...(canEditUserAccess ? accessColumns : [])
  ]

  const inviteSchema = Yup.object().shape({
    firstName: Yup.string().max(128).required('Please Enter a First Name'),
    lastName: Yup.string().max(128).required('Please Enter a Last Name'),
    email: Yup.string().email().required('Please Enter an Email'),
    password: Yup.string().required('Please Enter a Password')
  })

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: ''
    },
    validationSchema: inviteSchema,
    onSubmit: (values: FormValues) => {
      apiPostOrganizationUsers(values.firstName, values.lastName, values.email, values.password)
        .then(response => {
          if (response.errors) {
            if (response.errors[0].name) {
              response.errors.forEach(function (e: ApiError) {
                formik.setFieldError(e.name, e.message)
              })
            } else {
              formik.setStatus(response.errors[0].message)
            }
          } else {
            dispatch(showSuccessToast(`Invited ${values.firstName} ${values.lastName}`))
            usersQuery.refetch()
            closeModal()
            formik.setStatus(null)
          }
          formik.setSubmitting(false)
        })
        .catch(response => {
          formik.setSubmitting(false)
          formik.setStatus('Error inviting user')
        })
    }
  })

  const closeModal = () => setIsModalVisible(false)
  const showModal = () => setIsModalVisible(true)

  let modal

  if (isModalVisible) {
    modal = (
      <EuiOverlayMask>
        <EuiModal onClose={closeModal} style={{ width: 500 }}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>Invite User</EuiModalHeaderTitle>
          </EuiModalHeader>

          <EuiModalBody>
            <EuiForm component='form' onSubmit={formik.handleSubmit} onChange={formik.handleChange} onBlur={formik.handleBlur} isInvalid={formik.status && !!formik.errors} error={formik.status}>
              <EuiFormRow label='First Name' fullWidth isInvalid={formik.touched.firstName && !!formik.errors.firstName} error={formik.errors.firstName}>
                <EuiFieldText name='firstName' defaultValue={formik.values.firstName} fullWidth isInvalid={formik.touched.firstName && !!formik.errors.firstName} />
              </EuiFormRow>

              <EuiFormRow label='Last Name' fullWidth isInvalid={formik.touched.lastName && !!formik.errors.lastName} error={formik.errors.lastName}>
                <EuiFieldText name='lastName' defaultValue={formik.values.lastName} fullWidth isInvalid={formik.touched.lastName && !!formik.errors.lastName} />
              </EuiFormRow>

              <EuiFormRow label='Email' fullWidth isInvalid={formik.touched.email && !!formik.errors.email} error={formik.errors.email}>
                <EuiFieldText name='email' icon='email' defaultValue={formik.values.email} placeholder='username@domain.com' fullWidth isInvalid={formik.touched.email && !!formik.errors.email} />
              </EuiFormRow>

              <EuiFormRow label='Password' fullWidth isInvalid={formik.touched.password && !!formik.errors.password} error={formik.errors.password}>
                <EuiFieldPassword name='password' fullWidth isInvalid={formik.touched.password && !!formik.errors.password} />
              </EuiFormRow>
              <EuiSpacer />
            </EuiForm>
          </EuiModalBody>

          <EuiModalFooter>
            <EuiButtonEmpty id='cancel' onClick={closeModal}>
              Cancel
            </EuiButtonEmpty>

            <EuiButton id='invite' onClick={() => formik.handleSubmit()} fill isLoading={formik.isSubmitting}>
              Invite
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      </EuiOverlayMask>
    )
  }

  let content

  if (usersQuery.data == null || usersQuery.isLoading) {
    content = <EuiLoadingContent lines={3} />
  } else {
    content = (
      <React.Fragment>
        <EuiButton id='inviteUser' onClick={showModal} type='secondary' size='s'>
          Invite User
        </EuiButton>
        <EuiSpacer />
        <EuiBasicTable loading={usersQuery.isFetching} items={usersQuery.data} columns={columns} itemId='id' />
        {modal}
      </React.Fragment>
    )
  }

  return <div>{content}</div>
}

export default UsersDrawer
